import { SKY_WORLD_ASSETS } from '../../../packages/domain/src/86-sky-world-scale.mjs';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import { validateSkyPrototypeRig } from '../../../packages/domain/src/84-sky-prototype-rig.mjs';
import { skyGuardianAnatomy } from '../../../packages/domain/src/83-sky-anatomy.mjs';
import { prepareSkyNostrils } from './160-sky-dragon-nostrils.mjs';

/** Own per-encounter skeletal clones for the configured original dragon.
 * Geometry/textures stay loader-owned; cloned skeletons and mixers release with
 * the encounter. Rig data and model bytes are supplied by the same asset release. */
export function createSkyDragonFactory(T, source, rawRig, guardian) {
  const rig = validateSkyPrototypeRig(rawRig);
  if (!rig) throw new Error('Prototype dragon rig data is invalid.');
  const live = new Set();
  function release(group) {
    const local = group.userData.prototypeDragon;
    if (!local || local.released) return;
    local.released = true;
    local.mixer.stopAllAction();
    local.mixer.uncacheRoot(local.model);
    local.nostrils?.dispose();
    for (const skeleton of local.skeletons) skeleton.dispose();
    for (const resource of local.talismanResources ?? []) resource.dispose();
    guardian.releaseEnemy(group);
    live.delete(group);
  }
  function enemy(item) {
    const model = cloneSkeleton(source.scene),
      group = new T.Group();
    group.add(model);
    group.scale.setScalar(rig.modelScale * SKY_WORLD_ASSETS.dragon.modelScale);
    group.userData.form = 'prototypeDragon';
    group.userData.profile = item.profile ?? 'serpent';
    group.userData.artVariant = 'first-dragon';
    group.userData.prototypeRig = rig;
    const nodes = new Map(),
      skeletons = new Set();
    model.traverse((node) => {
      nodes.set(node.userData.name ?? node.name, node);
      if (node.isSkinnedMesh) skeletons.add(node.skeleton);
    });
    const mixer = new T.AnimationMixer(model);
    const local = {
      model,
      mixer,
      skeletons,
      nodes,
      actions: new Map(),
      roarJoints: [],
      released: false,
    };
    group.userData.prototypeDragon = local;
    live.add(group);
    try {
      const controlled = new Set();
      for (const joint of rig.chain) {
        const node = nodes.get(joint.name);
        if (!node) throw new Error(`Prototype dragon is missing rig node ${joint.name}.`);
        if (joint.parent >= 0 && node.parent !== nodes.get(rig.chain[joint.parent].name))
          throw new Error(`Prototype dragon ancestry differs at ${joint.name}.`);
        for (const [actual, expected] of [
          [node.position.toArray(), joint.position],
          [node.quaternion.toArray(), joint.quaternion],
          [node.scale.toArray(), joint.scale],
        ])
          if (actual.some((v, axis) => Math.abs(v - expected[axis]) > 0.0001))
            throw new Error(`Prototype dragon rest transform differs at ${joint.name}.`);
        controlled.add(node.name);
      }
      for (const clip of source.animations) {
        if (![rig.clip, 'Attack', 'Death'].includes(clip.name)) continue;
        if (!Number.isFinite(clip.duration) || clip.duration <= 0)
          throw new Error('Prototype dragon animation duration is invalid.');
        // Canonical hit-chain poses are applied below. Imported flight tracks
        // may animate wings/limbs, but cannot silently move collision-bearing bones.
        const tracks = clip.tracks.filter(
          (track) => !controlled.has(track.name.slice(0, track.name.lastIndexOf('.'))),
        );
        if (clip.name === 'Attack')
          for (const track of tracks) {
            const name = track.name.slice(0, track.name.lastIndexOf('.'));
            const node = [...nodes.values()].find((candidate) => candidate.name === name);
            if (node && /jaw/i.test(name) && track.name.endsWith('.quaternion'))
              local.roarJoints.push({
                node,
                quaternion: new T.Quaternion().fromArray(
                  track.createInterpolant().evaluate(clip.duration * 0.5),
                ),
              });
          }
        const filtered = new T.AnimationClip(clip.name, clip.duration, tracks);
        const action = mixer.clipAction(filtered);
        action.play();
        local.actions.set(clip.name, { action, duration: clip.duration });
      }
      if (!local.actions.has(rig.clip)) throw new Error('Prototype dragon flight clip is missing.');
      guardian.attachWeakpoint(group);
      if (group.userData.anatomicalTargets?.length !== 1)
        throw new Error('Prototype dragon requires one authored cranial target.');
      const socket = group.userData.anatomicalTargets[0].node;
      if (
        socket.parent !== nodes.get(rig.chain[rig.socket.bone].name) ||
        socket.position
          .toArray()
          .some((v, axis) => Math.abs(v - rig.socket.position[axis]) > 0.0001)
      )
        throw new Error(
          'Prototype dragon cranial socket differs from its canonical bone attachment.',
        );
      guardian.prepareGuardian(group);
      local.nostrils = prepareSkyNostrils(T, model, nodes.get('tripo::Head_2'));
      // The crest is the authored precision target; the mouth emits breath.
      // Private ornament resources avoid altering the shared imported asset.
      group.updateMatrixWorld(true);
      const socketScale = socket.getWorldScale(new T.Vector3());
      const ornamentScale = Math.max(socketScale.x, socketScale.y, socketScale.z);
      const jewelGeometry = new T.OctahedronGeometry(0.11 / ornamentScale, 1);
      const frameGeometry = new T.TorusGeometry(0.15 / ornamentScale, 0.022 / ornamentScale, 8, 24);
      const jewelMaterial = new T.MeshStandardMaterial({
        color: '#bafff0',
        emissive: '#43ffcf',
        emissiveIntensity: 1.2,
        metalness: 0.45,
        roughness: 0.2,
      });
      const frameMaterial = new T.MeshStandardMaterial({
        color: '#e9b75f',
        emissive: '#68431b',
        metalness: 0.85,
        roughness: 0.25,
      });
      const jewel = new T.Mesh(jewelGeometry, jewelMaterial);
      const frame = new T.Mesh(frameGeometry, frameMaterial);
      socket.add(jewel, frame);
      local.talisman = jewel;
      local.talismanResources = [jewelGeometry, frameGeometry, jewelMaterial, frameMaterial];
      return group;
    } catch (error) {
      release(group);
      throw error;
    }
  }
  function animate(group, item, reduced) {
    const local = group.userData.prototypeDragon;
    if (!local || local.released) return;
    const anatomy = skyGuardianAnatomy({ ...item, artVariant: 'first-dragon', prototypeRig: rig });
    const age = Math.max(0, item.age ?? 0) + (item.animationOffset ?? 0);
    const roar = Math.max(0, Math.min(1, item.entryRoar ?? 0));
    const attack = local.actions.has('Attack')
      ? Math.min(0.45, Math.max(0, item.charge ?? 0) * 0.45)
      : 0;
    const dying = item.defeated && local.actions.has('Death');
    for (const [name, { action, duration }] of local.actions) {
      const weight = dying
        ? name === 'Death'
          ? 1
          : 0
        : name === rig.clip
          ? 1 - attack
          : name === 'Attack'
            ? attack
            : 0;
      action.enabled = true;
      action.setEffectiveWeight(weight);
      action.time = reduced
        ? 0
        : name === 'Death'
          ? Math.min(item.deathAge ?? 0, duration - 0.000001)
          : (name === 'Attack' ? (item.phaseAge ?? age) : age) % duration;
    }
    local.mixer.update(0);
    // Hold only the independently rigged mouth at its roar peak. Flight wings
    // and tail retain their advancing clip rather than freezing the whole body.
    if (!dying)
      for (const joint of local.roarJoints)
        joint.node.quaternion.slerp(
          joint.quaternion,
          Math.max(roar * 0.95, (item.charge ?? 0) * 0.9),
        );
    for (const joint of anatomy.rigPose) {
      const node = local.nodes.get(joint.name);
      node.position.fromArray(joint.position);
      node.quaternion.fromArray(joint.quaternion);
      node.scale.fromArray(joint.scale);
    }
    local.nostrils.update(anatomy.breathing?.nostrilFlare ?? 0);
    group.rotation.set(...anatomy.rootRotation);
    group.updateMatrixWorld(true);
    for (const skeleton of local.skeletons) skeleton.update();
    guardian.updateDamage(group, item, reduced);
    guardian.updateIris(group, { ...item, anatomy }, reduced);
    const charge = Math.max(0, Math.min(1, item.charge ?? 0));
    const sinceAttack = (item.age ?? 0) - (item.lastAttackAge ?? -10);
    const flash = sinceAttack >= 0 ? Math.max(0, 1 - sinceAttack / 0.22) : 0;
    local.talisman.material.emissiveIntensity = dying ? 0.15 : 1.2 + charge * 3 + flash * 5;
    local.talisman.scale.setScalar(reduced ? 1 : 1 + charge * 0.18 + flash * 0.3);
  }
  return {
    enemy,
    animate,
    release,
    dispose() {
      for (const group of [...live]) release(group);
    },
  };
}
